<div class="dialog-container" [class.inline-mode]="isInlineMode">
  <!-- Header -->
  <div class="dialog-header" *ngIf="!isInlineMode">
    <button *ngIf="currentStage !== 'goal-selection' && !isEditMode" mat-icon-button class="back-button" (click)="onBack()">
      <mat-icon svgIcon="arrow-left"></mat-icon>
    </button>
    <h2>{{ getDialogTitle() }}</h2>
  </div>

  <!-- Content -->
  <div class="dialog-content">
    <!-- Stage 1: Goal Selection -->
    <div *ngIf="currentStage === 'goal-selection'" class="item-selection-stage">
      <div class="stage-header content-box">
        <div class="tabs flex-start gap-sm">
          <button class="tab-button" 
                  [class.active]="!showFavorites"
                  (click)="toggleShowFavorites(false)">
            <mat-icon>apps</mat-icon>
            All items
          </button>
          <button class="tab-button" 
                  [class.active]="showFavorites"
                  (click)="toggleShowFavorites(true)">
            <mat-icon>star_border</mat-icon>
            Favorites
          </button>
        </div>
        <div class="search-and-toggle-container flex-start gap-sm">
          <div class="view-mode-toggle">
            <button
              class="view-mode-toggle-btn"
              [class.active]="layoutService.getViewMode() === 'simple'"
              (click)="layoutService.setViewMode('simple')">
              Simple
            </button>
            <button
              class="view-mode-toggle-btn"
              [class.active]="layoutService.getViewMode() === 'advanced'"
              (click)="layoutService.setViewMode('advanced')">
              Advanced
            </button>
          </div>

          <div class="vertical-divider"></div>
          
          <div class="search-box card-white rounded-sm flex-start gap-sm">
            <mat-icon svgIcon="search"></mat-icon>
            <input type="text" 
                   placeholder="Filter by goal or category" 
                   class="search-input"
                   [(ngModel)]="searchQuery">
          </div>
        </div>
      </div>

      <div class="items-content-wrapper">
        <!-- Scroll Gradients -->
        <div class="scroll-gradient scroll-gradient-top" [class.visible]="hasScrollTop"></div>
        <div class="scroll-gradient scroll-gradient-bottom" [class.visible]="hasScrollBottom"></div>

        <div class="items-content" 
             (scroll)="onGoalsContentScroll($event)">
        <!-- Show message when no favorites exist -->
        <div *ngIf="isDataReady && showFavorites && favoriteGoals.size === 0" class="help-section">
          <mat-icon>star_border</mat-icon>
          <h3>No favorites yet</h3>
          <p>Click the star icon on any goal to add it to your favorites.</p>
        </div>

        <!-- Show message when search returns no results -->
        <div *ngIf="isDataReady && !showFavorites && filteredCategories.length === 0 && searchQuery.trim() && !error" class="help-section">
          <mat-icon svgIcon="search"></mat-icon>
          <h3>No goals found</h3>
          <p>Try adjusting your search terms.</p>
        </div>

        <!-- Show message when no categories available (after loading) -->
        <div *ngIf="isDataReady && !error && filteredCategories.length === 0 && !searchQuery.trim()" class="help-section">
          <mat-icon svgIcon="info-circle"></mat-icon>
          <h3>No goals available</h3>
          <p>No available goals were found.</p>
        </div>

        <!-- Loading state -->
        <div *ngIf="!isDataReady" class="loading-container">
          <mat-spinner diameter="40"></mat-spinner>
          <p>Loading available goals...</p>
        </div>

        <!-- Error state -->
        <div *ngIf="error && isDataReady" class="error-container">
          <mat-icon>error</mat-icon>
          <p>{{ error }}</p>
          <button mat-button (click)="loadGoalTemplates()">Retry</button>
        </div>

        <!-- Tasks grouped by category -->
        <div class="items-content-grouped" *ngIf="isDataReady">
          <mat-expansion-panel *ngFor="let category of filteredCategories" 
                               class="help-expansion-panel"
                               [expanded]="isCategoryExpanded(category)"
                               (opened)="setCategoryExpanded(category, true)"
                               (closed)="setCategoryExpanded(category, false)">
            <mat-expansion-panel-header>
              <mat-panel-title>
                {{ formatCategoryTitle(category) }}
                <span class="task-count">{{ getGoalTemplatesForCategory(category).length }} {{ getGoalTemplatesForCategory(category).length === 1 ? 'goal' : 'goals' }}</span>
              </mat-panel-title>
            </mat-expansion-panel-header>
            
            <!-- Tasks in this category -->
            <div class="items-grid">
              <div *ngFor="let task of getGoalTemplatesForCategory(category)" 
                   class="card-selectable task-card"
                   [class.selected]="formData.goalType === task.key"
                   [class.locked]="!isGoalSupported(task.key)"
                   (click)="selectGoalTemplate(task)">
                <div class="item-header">
                  <mat-icon class="item-icon goal-icon" svgIcon="flag"></mat-icon>
                  <div class="item-info">
                    <h5>{{ task.metadata.name || task.key }}</h5>
                    <!-- <span class="goal-type">{{ task.key }}</span> -->
                  </div>
                  <div class="item-header-actions">
                    <h5 *ngIf="!isGoalSupported(task.key)" class="unsupported-label">Upgrade Required</h5>
                    <mat-icon *ngIf="!isGoalSupported(task.key)" class="lock-icon" matTooltip="Requires plan upgrade" svgIcon="lock"></mat-icon>
                    <span *ngIf="isGoalSupported(task.key) && task.isPreview" class="preview-tag">Preview</span>
                    <button *ngIf="isGoalSupported(task.key)"
                            class="favorite-btn" 
                            mat-icon-button
                            (click)="toggleGoalTemplateFavorite(task.key, $event)">
                      <mat-icon>{{ isGoalTemplateFavorite(task.key) ? 'star' : 'star_border' }}</mat-icon>
                    </button>
                  </div>
                </div>
                <p class="item-description">{{ getGoalTemplateDescription(task) }}</p>
              </div>
            </div>
          </mat-expansion-panel>
        </div>
      </div>
      </div>
    </div>

    <!-- Stage 2: Goal Configuration -->
    <div *ngIf="currentStage === 'goal-configuration'" class="goal-configuration-stage">
      <div class="configuration-content">
        
        <!-- Error Display -->
        <div *ngIf="error && currentStage === 'goal-configuration'" class="error-container" style="margin-bottom: 16px;">
          <mat-icon>error</mat-icon>
          <p>{{ error }}</p>
        </div>
        
        <!-- Goal Details Section - Full Width at Top -->
        <div class="goal-info-section box-light-grey" style="margin-bottom: 24px;">
          <div class="flex justify-between align-center" style="margin-bottom: 16px;">
            <h4 style="margin: 0;">Goal Details</h4>
            
            <!-- Goal Active Toggle (Edit Mode Only) -->
            <div *ngIf="isEditMode" class="goal-status-toggle flex align-center gap-8">
              <span class="toggle-label">{{ formData.isActive ? 'Active' : 'Inactive' }}</span>
              <mat-slide-toggle
                [(ngModel)]="formData.isActive"
                color="primary"
                [matTooltip]="formData.isActive ? 'This goal is active' : 'This goal is inactive'"
                matTooltipPosition="left">
              </mat-slide-toggle>
            </div>
          </div>
          
          <!-- Goal Name Input -->
          <mat-form-field appearance="outline" class="full-width goal-name-field">
            <mat-label>Goal Name</mat-label>
            <input matInput 
                   [(ngModel)]="formData.name"
                   placeholder="Enter a name for this goal"
                   maxlength="100">
            <img src="assets/icons/Pencil.svg" alt="" matSuffix class="edit-icon">
            <mat-hint>Give this goal a descriptive name</mat-hint>
          </mat-form-field>
          
          <!-- Column Selection with Chip Selector -->
          <div class="target-columns-field">
            <label class="target-columns-label">
              Select Columns
              <span class="required-indicator">*</span>
            </label>
            <p class="field-description">Select one or more columns for this goal's tasks</p>
            
            <div class="columns-selection-container">
              <div class="selection-actions">
                <button type="button" mat-button (click)="selectAllGoalColumns()" class="select-all-btn">
                  <mat-icon svgIcon="check_box"></mat-icon>
                  Select All
                </button>
                <button type="button" mat-button (click)="deselectAllGoalColumns()" class="deselect-all-btn">
                  <mat-icon svgIcon="check_box_outline_blank"></mat-icon>
                  Deselect All
                </button>
              </div>
              
              <!-- Suggested columns grid -->
              <div class="task-columns-selector compact" *ngIf="suggestedGoalColumns.length > 0">
                <div *ngFor="let column of suggestedGoalColumns" class="column-chip-item">
                  <button 
                    type="button"
                    class="column-chip"
                    [class.included]="isGoalColumnSelected(column.name)"
                    [class.excluded]="!isGoalColumnSelected(column.name)"
                    (click)="toggleGoalColumnSelection(column.name)">
                    <span class="column-name">{{ column.name }}</span>
                    <span class="column-type-badge" [class]="'type-' + column.dataType.toLowerCase()">{{ getFriendlyDataTypeName(column.dataType) }}</span>
                  </button>
                </div>
              </div>
              
              <!-- Separator between suggested and other columns -->
              <hr *ngIf="suggestedGoalColumns.length > 0 && remainingGoalColumns.length > 0" class="suggested-separator">
              
              <!-- Other columns grid -->
              <div class="task-columns-selector compact" *ngIf="remainingGoalColumns.length > 0">
                <div *ngFor="let column of remainingGoalColumns" class="column-chip-item">
                  <button 
                    type="button"
                    class="column-chip"
                    [class.included]="isGoalColumnSelected(column.name)"
                    [class.excluded]="!isGoalColumnSelected(column.name)"
                    (click)="toggleGoalColumnSelection(column.name)">
                    <span class="column-name">{{ column.name }}</span>
                    <span class="column-type-badge" [class]="'type-' + column.dataType.toLowerCase()">{{ getFriendlyDataTypeName(column.dataType) }}</span>
                  </button>
                </div>
              </div>
              
              <div class="no-columns-message" *ngIf="sortedGoalColumns.length === 0">
                <mat-icon svgIcon="info"></mat-icon>
                <p>No columns available. Please ensure your data source has been loaded.</p>
              </div>
            </div>
            
            <!-- Validation message for required columns -->
            <div *ngIf="shouldShowColumnValidationError()" class="validation-message">
              <mat-icon>warning</mat-icon>
              <span>Please select at least one column to proceed</span>
            </div>
          </div>
        </div>
        
        <!-- Advanced Section - Collapsible -->
        <mat-expansion-panel class="help-expansion-panel" [expanded]="advancedOptionsExpanded">
          <mat-expansion-panel-header>
            <mat-panel-title>
              <mat-icon class="panel-icon">settings</mat-icon>
              Advanced
            </mat-panel-title>
          </mat-expansion-panel-header>
          
          <div class="configuration-layout with-task-view">
            <!-- Left Side: Task Selection with Toggles -->
            <div class="task-selection-section">
              <!-- Task List with Toggles -->
              <div class="goal-tasks-section box-light-grey" [class.disabled-section]="!hasColumnsSelected()">
                <div class="tasks-header flex align-center gap-sm">
                  <h4 class="tasks-title">Tasks for this Goal</h4>
                  <p class="subtitle tasks-subtitle">Click on Tasks to fine tune settings</p>
              </div>
              
              <!-- Loading state -->
              <div *ngIf="loadingTasks" class="help-section">
                <mat-icon>hourglass_empty</mat-icon>
                <p>Loading tasks...</p>
              </div>
              
              <!-- No tasks available -->
              <div *ngIf="!loadingTasks && taskCatalogWithState.length === 0" class="help-section">
                <mat-icon svgIcon="info-circle"></mat-icon>
                <p>No tasks available for this goal.</p>
              </div>
              
              <!-- Task list with toggles -->
              <div *ngIf="!loadingTasks && taskCatalogWithState.length > 0" class="tasks-list-with-toggles">
                <div *ngFor="let task of taskCatalogWithState" 
                     class="task-toggle-item"
                     [class.selected]="selectedTaskForView === task"
                     [class.mandatory]="task.mandatory"
                     [class.disabled-item]="!hasColumnsSelected()"
                     (click)="selectTaskForViewing(task)">
                  <div class="task-toggle-content">
                    <div class="task-info">
                      <span class="task-name">{{ task.name }}</span>
                      <span class="task-badge" *ngIf="task.mandatory">Mandatory</span>
                      <span class="task-badge task-badge-optional" *ngIf="!task.mandatory && !task.required">Optional</span>
                    </div>
                    <mat-slide-toggle
                      *ngIf="!task.mandatory && !task.required"
                      [(ngModel)]="task.enabled"
                      (ngModelChange)="onTaskEnabledChange(task, $event)"
                      [disabled]="!hasColumnsSelected()"
                      (click)="$event.stopPropagation()"
                      color="primary">
                    </mat-slide-toggle>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Right Side: Task Details Panel -->
          <div class="task-detail-panel">
            <div *ngIf="selectedTaskForView" class="task-detail-content box-light-grey full-height">
              <div class="task-detail-header">
                <div class="header-title-section">
                  <h4>{{ selectedTaskForView.name }}</h4>
                  <span class="complexity-badge" *ngIf="selectedTaskForView.complexity">
                    Complexity: {{ selectedTaskForView.complexity }}
                  </span>
                </div>
                <!-- Removed status badge per UX request -->
              </div>
              
              <div class="task-detail-body" [class.task-detail-body--disabled]="!selectedTaskForView.enabled">
                <div *ngIf="!selectedTaskForView.enabled" class="task-disabled-overlay">
                  <mat-icon>toggle_off</mat-icon>
                  <p>This task is disabled. Enable it using the toggle to configure its parameters.</p>
                </div>
                <div class="task-detail-section">
                  <h5>Description</h5>
                  <p>{{ selectedTaskForView.description || 'No description available' }}</p>
                </div>
                
                <div class="task-detail-section" *ngIf="getTaskParameters(selectedTaskForView).length > 0">
                  <h5>Parameters</h5>
                  <div class="task-parameters-editor">
                    <div *ngFor="let param of getTaskParameters(selectedTaskForView)" class="parameter-editor-field">
                      
                      <!-- Select dropdown for enum parameters (non-array) -->
                      <div *ngIf="param.enum && param.enum.length > 0 && param.type !== 'array'">
                        <mat-form-field appearance="outline" class="full-width">
                          <mat-label>
                            {{ param.name || param.key }}
                            <span *ngIf="isParameterRequired(param)" class="required-indicator">*</span>
                          </mat-label>
                          <mat-select [(ngModel)]="param.value"
                                      (ngModelChange)="onParameterChange(selectedTaskForView)"
                                      [placeholder]="param.default || 'Select value'"
                                      [required]="isParameterRequired(param)">
                            <mat-option *ngFor="let option of param.enum" [value]="option">
                              {{ option }}
                            </mat-option>
                          </mat-select>
                        </mat-form-field>
                        <p *ngIf="param.description" class="parameter-description">{{ param.description }}</p>
                      </div>
                      
                      <!-- Multi-select dropdown for array parameters with enum -->
                      <div *ngIf="param.enum && param.enum.length > 0 && param.type === 'array'">
                        <mat-form-field appearance="outline" class="full-width">
                          <mat-label>
                            {{ param.name || param.key }}
                            <span *ngIf="isParameterRequired(param)" class="required-indicator">*</span>
                          </mat-label>
                          <mat-select [(ngModel)]="param.value"
                                      (ngModelChange)="onParameterChange(selectedTaskForView)"
                                      [placeholder]="param.default && param.default.length > 0 ? param.default.join(', ') : 'Select options'"
                                      [required]="isParameterRequired(param)"
                                      multiple>
                            <mat-option *ngFor="let option of param.enum" [value]="option">
                              {{ option }}
                            </mat-option>
                          </mat-select>
                        </mat-form-field>
                        <p *ngIf="param.description" class="parameter-description">{{ param.description }}</p>
                      </div>
                      
                      <!-- Checkbox for boolean parameters -->
                      <div *ngIf="!param.enum && param.type === 'boolean'">
                        <mat-checkbox [(ngModel)]="param.value"
                                      (ngModelChange)="onParameterChange(selectedTaskForView)"
                                      [required]="isParameterRequired(param)">
                          {{ param.name || param.key }}
                          <span *ngIf="isParameterRequired(param)" class="required-indicator">*</span>
                        </mat-checkbox>
                        <p *ngIf="param.description" class="parameter-description">{{ param.description }}</p>
                      </div>
                      
                      <!-- Multi-select for array parameters -->
                      <div *ngIf="!param.enum && param.type === 'array'">
                        <mat-form-field appearance="outline" class="full-width">
                          <mat-label>
                            {{ param.name || param.key }}
                            <span *ngIf="isParameterRequired(param)" class="required-indicator">*</span>
                          </mat-label>
                          <mat-select [(ngModel)]="param.value"
                                      (ngModelChange)="onParameterChange(selectedTaskForView)"
                                      [placeholder]="'Select columns'"
                                      [required]="isParameterRequired(param)"
                                      multiple>
                            <mat-option *ngFor="let column of (formData.targetColumns && formData.targetColumns.length ? formData.targetColumns : availableColumns)" [value]="column">
                              {{ column }}
                            </mat-option>
                          </mat-select>
                        </mat-form-field>
                        <p *ngIf="param.description" class="parameter-description">{{ param.description }}</p>
                      </div>

                      <!-- Single-column select for parameters explicitly marked as 'column' -->
                      <div *ngIf="!param.enum && param.type === 'column'">
                        <mat-form-field appearance="outline" class="full-width">
                          <mat-label>
                            {{ param.name || param.key }}
                            <span *ngIf="isParameterRequired(param)" class="required-indicator">*</span>
                          </mat-label>
                          <mat-select [(ngModel)]="param.value"
                                      (ngModelChange)="onParameterChange(selectedTaskForView)"
                                      [placeholder]="param.default || 'Select column'"
                                      [required]="isParameterRequired(param)">
                            <mat-option *ngFor="let column of availableColumns" [value]="column">
                              <span>{{ column }}</span>
                              <span *ngIf="getColumnDataType(column)" class="column-data-type"> ({{ getColumnDataType(column) }})</span>
                            </mat-option>
                          </mat-select>
                        </mat-form-field>
                        <p *ngIf="param.description" class="parameter-description">{{ param.description }}</p>
                      </div>
                      
                      <!-- Number input for number parameters -->
                      <div *ngIf="!param.enum && param.type === 'number'">
                        <mat-form-field appearance="outline" class="full-width">
                          <mat-label>
                            {{ param.name || param.key }}
                            <span *ngIf="isParameterRequired(param)" class="required-indicator">*</span>
                          </mat-label>
                          <input matInput
                                 type="number"
                                 [(ngModel)]="param.value"
                                 (ngModelChange)="onParameterChange(selectedTaskForView)"
                                 [placeholder]="param.default || 'Enter number'"
                                 [required]="isParameterRequired(param)">
                        </mat-form-field>
                        <p *ngIf="param.description" class="parameter-description">{{ param.description }}</p>
                      </div>
                      
                      <!-- Text input for string parameters without enum -->
                      <div *ngIf="!param.enum && param.type === 'string'">
                        <mat-form-field appearance="outline" class="full-width">
                          <mat-label>
                            {{ param.name || param.key }}
                            <span *ngIf="isParameterRequired(param)" class="required-indicator">*</span>
                          </mat-label>
                          <input matInput
                                 [(ngModel)]="param.value"
                                 (ngModelChange)="onParameterChange(selectedTaskForView)"
                                 [placeholder]="param.default || 'Enter value'"
                                 [required]="isParameterRequired(param)">
                        </mat-form-field>
                        <p *ngIf="param.description" class="parameter-description">{{ param.description }}</p>
                      </div>
                      
                      <!-- Default input for other parameter types -->
                      <div *ngIf="!param.enum && param.type !== 'string' && param.type !== 'boolean' && param.type !== 'array' && param.type !== 'number' && param.type !== 'column'">
                        <mat-form-field appearance="outline" class="full-width">
                          <mat-label>
                            {{ param.name || param.key }}
                            <span *ngIf="isParameterRequired(param)" class="required-indicator">*</span>
                          </mat-label>
                          <input matInput
                                 [(ngModel)]="param.value"
                                 (ngModelChange)="onParameterChange(selectedTaskForView)"
                                 [placeholder]="param.default || 'Enter value'"
                                 [required]="isParameterRequired(param)">
                        </mat-form-field>
                        <p *ngIf="param.description" class="parameter-description">{{ param.description }}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="task-detail-section" *ngIf="getTaskParameters(selectedTaskForView).length === 0">
                  <p class="no-parameters-text">This task has no configurable parameters.</p>
                </div>
                
                <!-- Column Selection Section -->
                <mat-expansion-panel *ngIf="formData.targetColumns && formData.targetColumns.length > 1" 
                                     class="help-expansion-panel"
                                     [expanded]="true">
                  <mat-expansion-panel-header>
                    <mat-panel-title>
                      <mat-icon class="panel-icon">tune</mat-icon>
                      Advanced: Column Selection
                    </mat-panel-title>
                  </mat-expansion-panel-header>
                  
                  <p class="section-hint">Click to exclude individual columns from this task.</p>
                    <div class="task-columns-selector compact">
                      <div *ngFor="let column of formData.targetColumns" class="column-chip-item">
                        <button 
                          type="button"
                          class="column-chip"
                          [class.included]="isColumnSelectedForTask(selectedTaskForView, column)"
                          [class.excluded]="!isColumnSelectedForTask(selectedTaskForView, column)"
                          [disabled]="selectedTaskForView.mandatory"
                          (click)="toggleColumnForTask(selectedTaskForView, column, !isColumnSelectedForTask(selectedTaskForView, column))">
                          <mat-icon class="status-icon" [svgIcon]="isColumnSelectedForTask(selectedTaskForView, column) ? 'check_circle' : 'cancel'"></mat-icon>
                          <span class="column-name">{{ column }}</span>
                        </button>
                      </div>
                    </div>
                    <p class="column-note" *ngIf="selectedTaskForView.mandatory">
                      <mat-icon>info</mat-icon>
                      Mandatory tasks require all selected columns
                    </p>
                </mat-expansion-panel>
              </div>
            </div>
            
            <!-- No task selected message -->
            <div *ngIf="!selectedTaskForView" class="no-task-selected box-light-grey full-height">
              <mat-icon>info</mat-icon>
              <p>Select a task to view its details</p>
            </div>
          </div>
          </div>
        
        </mat-expansion-panel>
      </div>
    </div>
  </div>

  <!-- Actions -->
  <div class="dialog-actions">
    <div class="action-buttons">
      <div class="left-buttons">
        <button *ngIf="currentStage === 'goal-configuration' && !isEditMode" 
                mat-button 
                class="back-button-step2" 
                (click)="onBack()">
          <mat-icon svgIcon="arrow-left"></mat-icon>
          Back
        </button>
      </div>
      <div class="right-buttons">
        <button mat-button (click)="onCancel()" class="cancel-button">
          Cancel
        </button>
        
        <button *ngIf="currentStage === 'goal-selection'" 
                mat-raised-button 
                color="primary" 
                [disabled]="!canProceed()"
                (click)="onNext()"
                class="next-button">
          Next
        </button>
        
        <button *ngIf="currentStage === 'goal-configuration'" 
                mat-raised-button 
                color="primary" 
                [disabled]="!canCreate() || loading"
                (click)="onConfirm()"
                class="confirm-button">
          <mat-icon *ngIf="loading">hourglass_empty</mat-icon>
          {{ getConfirmButtonText() }}
        </button>
      </div>
    </div>
  </div>
</div>
